import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import useConfirm from '../ui/useConfirm';
import RoutingHistoryPanel from './RoutingHistoryPanel';
import './RoteiroGerar.css';
import {
  NARRATIVAS_USINAGEM,
  OPCAO_NARRATIVA_MANUAL,
  alertaNarrativa,
  narrativaUsinagemEhPredefinida,
  normalizarNarrativa,
  operacaoEhUsinagem,
} from './narrativas';

const OPERACOES_LISTA = [
  { operacao: "012",   descr: "TERCEIRIZACAO CORTE + PINTURA UV", maquina: "7501", multiplos: 1 },
  { operacao: "013",   descr: "TERCEIRIZACAO CORTE",              maquina: "7502", multiplos: 1 },
  { operacao: "002",   descr: "CORTAR",                         maquina: "1050", multiplos: 1 },
  { operacao: "004",   descr: "USINAR",                         maquina: "2050", multiplos: 1 },
  { operacao: "004.2", descr: "USINAR (2)",                     maquina: "2050", multiplos: 1 },
  { operacao: "027",   descr: "RECORTAR",                       maquina: "2050", multiplos: 1 },
  { operacao: "027.2", descr: "RECORTAR (2)",                   maquina: "1010", multiplos: 1 },
  { operacao: "039",   descr: "DESTOPAR",                       maquina: "8070", multiplos: 1 },
  { operacao: "039.3", descr: "DESTOPAR (2)",                   maquina: "8070", multiplos: 1 },
  { operacao: "039.2", descr: "DESTOPAR 45°",                   maquina: "8071", multiplos: 1 },
  { operacao: "006",   descr: "COLAR BORDA",                    maquina: "1550", multiplos: 1 },
  { operacao: "016",   descr: "COLAR BORDA LARGURA 1",          maquina: "1550", multiplos: 1 },
  { operacao: "017",   descr: "COLAR BORDA LARGURA 2",          maquina: "1550", multiplos: 1 },
  { operacao: "018",   descr: "COLAR BORDA COMPRIMENTO 1",      maquina: "1550", multiplos: 1 },
  { operacao: "019",   descr: "COLAR BORDA COMPRIMENTO 2",      maquina: "1550", multiplos: 1 },
  { operacao: "061",   descr: "COLAR BORDA LARGURA 1 SOFT",     maquina: "1509", multiplos: 1 },
  { operacao: "062",   descr: "COLAR BORDA LARGURA 2 SOFT",     maquina: "1509", multiplos: 1 },
  { operacao: "063",   descr: "COLAR BORDA COMPRIMENTO 1 SOFT", maquina: "1509", multiplos: 1 },
  { operacao: "064",   descr: "COLAR BORDA COMPRIMENTO 2 SOFT", maquina: "1509", multiplos: 1 },
  { operacao: "016.3", descr: "ESQUADREJAR LARGURA 1",          maquina: "1560", multiplos: 1 },
  { operacao: "017.3", descr: "ESQUADREJAR LARGURA 2",          maquina: "1560", multiplos: 1 },
  { operacao: "018.3", descr: "ESQUADREJAR COMPRIMENTO 1",      maquina: "1560", multiplos: 1 },
  { operacao: "019.3", descr: "ESQUADREJAR COMPRIMENTO 2",      maquina: "1560", multiplos: 1 },
  { operacao: "028",   descr: "FURAÇÃO CRUZADA",                maquina: "4050", multiplos: 1 },
  { operacao: "029",   descr: "FURAÇÃO SIMULTÂNEA",             maquina: "4050", multiplos: 1 },
  { operacao: "065",   descr: "FURAÇÃO TOPO TRASEIRO",          maquina: "4050", multiplos: 1 },
  { operacao: "065.2", descr: "FURAÇÃO TOPO TRASEIRO (2)",      maquina: "4050", multiplos: 1 },
  { operacao: "031",   descr: "FURAÇÃO PASSA FIO",              maquina: "4050", multiplos: 1 },
  { operacao: "055",   descr: "FURAÇÃO 45°",                    maquina: "4014", multiplos: 1 },
  { operacao: "043",   descr: "GRAMPEAR 45°",                   maquina: "8600", multiplos: 1 },
  { operacao: "003",   descr: "GRAMPEAR",                       maquina: "2501", multiplos: 1 },
  { operacao: "042",   descr: "ESTAMPAGEM LARGURA 1",           maquina: "8020", multiplos: 1 },
  { operacao: "042.2", descr: "ESTAMPAGEM LARGURA 2",           maquina: "8020", multiplos: 1 },
  { operacao: "008",   descr: "CARIMBAR",                       maquina: "6501", multiplos: 1 },
  { operacao: "024",   descr: "PINTURA DE SUPERFICIE LADO 1",   maquina: "5550", multiplos: 1 },
  { operacao: "025",   descr: "PINTURA DE SUPERFICIE LADO 2",   maquina: "5550", multiplos: 1 },
  { operacao: "032",   descr: "CHECK-POINT",                    maquina: "1100", multiplos: 1 },
];

const OPERACOES_BORDA_NORMAL = new Set(["006", "016", "017", "018", "019"]);
const NARRATIVA_COLADEIRA_DUPLA = "PROCESSAR BORDA NA ESQUADRA - RETIRAR 2MM NA FRESA";

const NOMES_MAQUINAS = Object.freeze({
  UEP: 'Unidade de Esforço de Produção',
  2003: 'TUPIA 503', 2002: 'TUPIA 502', 2001: 'TUPIA 501', 2050: 'TUPIA',
  7511: 'TERCEIRIZACAO RECOBRIMENTO COURO',
  7514: 'TERCEIRIZACAO PRODUCAO',
  7506: 'TERCEIRIZACAO PINTURA PU',
  7509: 'TERCEIRIZACAO MONTAGEM TV',
  7503: 'TERCEIRIZACAO KIT ACESSORIO',
  7510: 'TERCEIRIZACAO FURACAO',
  7512: 'TERCEIRIZACAO ESTOFAMENTO',
  7508: 'TERCEIRIZACAO CORTE TELA',
  7501: 'TERCEIRIZACAO CORTE + PINTURA UV',
  7513: 'TERCEIRIZACAO CORTE + PINT. UV + CARIMBO',
  7502: 'TERCEIRIZACAO CORTE',
  7507: 'TERCEIRIZACAO CARIMBAGEM',
  7505: 'TERCEIRIZACAO BORDA MESA',
  7504: 'SERRARIA',
  1007: 'SERRA 209', 1006: 'SERRA 208', 1005: 'SERRA 207',
  8072: 'SER 1517', 8071: 'SER 1509', 8070: 'SER 1508',
  1050: 'SECCION.', 1011: 'SECC 212', 1012: 'SECC 211',
  1009: 'SECC 205', 1004: 'SECC 204', 1003: 'SECC 203',
  1002: 'SECC 202', 1001: 'SECC 201',
  8300: 'RECOBRIDORA', 8032: 'REC 1516', 8031: 'REC 1510', 8030: 'REC 1504',
  8920: 'PRENSAR PORTA', 7001: 'PRENSA',
  1013: 'MULTI-SERRA 213', 1010: 'MULTI-SERRA 210',
  8400: 'MQ.EMB.PANOTEC 1601', 8910: 'MONTAGEM PORTA',
  6003: 'MONTAGEM (SETOR DE EMBALAGEM)',
  8100: 'MOLDUREIRA', 8012: 'MOL 1515', 8011: 'MOL 1511', 8010: 'MOL 1502',
  2500: 'MESA DE GRAMPEACAO', 2503: 'MESA 03', 2502: 'MESA 02', 2501: 'MESA 01',
  MANUT: 'MANUTENÇÃO', 2005: 'LIXA 02', 2004: 'LIXA 01',
  8501: 'LINHA MONTAGEM DE KIT ACESSORIO',
  5504: 'LINHA 1004', 5503: 'LINHA 1003', 5502: 'LINHA 1002', 5501: 'LINHA 1001',
  4501: 'LABO 801', 5550: 'IMPRES.', 8050: 'HOT 1506',
  8930: 'GRAMPEAR PORTA', 8600: 'GRAMPEADORA 45º',
  8062: 'GRA 1513', 8061: 'GRA 1512', 8060: 'GRA 1507',
  4050: 'FURAD.', 4016: 'FURA 616', 4014: 'FURA 614', 4013: 'FURA 613',
  4012: 'FURA 612', 4011: 'FURA 611', 4010: 'FURA 610', 4009: 'FURA 609',
  4008: 'FURA 608', 4007: 'FURA 607', 4006: 'FURA 606', 4005: 'FURA 605',
  4004: 'FURA 604', 4003: 'FURA 603', 4002: 'FURA 602', 4001: 'FURA 601',
  8900: 'FRESA COPIADORA', 8092: 'FRES. COP. 1522', 8091: 'FRES. COP. 1518',
  8081: 'FATIADORA 2 (1520)', 8080: 'FATIADORA 1 (1519)',
  1201: 'ESTOQUE PEÇAS III', 1200: 'ESTOQUE PEÇAS II', 1100: 'ESTOQUE PECAS I',
  8020: 'ESQ 1503', 8800: 'EMBAL. RIPADO', 6050: 'EMBAL.',
  6002: 'EMBAL 1102', 6001: 'EMBAL 1101', 8040: 'DES 1505',
  1511: 'COLA 311', 1510: 'COLA 310', 1509: 'COLA 309', 1508: 'COLA 308',
  1507: 'COLA 307', 1506: 'COLA 306', 1505: 'COLA 305', 1504: 'COLA 304',
  1503: 'COLA 303', 1502: 'COLA 302', 1501: 'COLA 301',
  1550: 'COL. UNI.', 1560: 'COL. DUP.', 3001: 'CEUS 401',
  3550: 'CEN. FUR.', 3502: 'CEFU 403', 3501: 'CEFU 402',
  6501: 'CARIMBO', 5001: 'CAPU 01', 1008: 'CALIB 206', 6502: 'ADESIVO',
});

function rotuloMaquina(codigo) {
  const nome = NOMES_MAQUINAS[codigo];
  return nome ? `${codigo} — ${nome}` : codigo;
}

function narrativaParaMaquina(op, maquina) {
  if (!OPERACOES_BORDA_NORMAL.has(op.operacao)) return op.narrativa || '';
  const semDupla = (op.narrativa || '')
    .replace(NARRATIVA_COLADEIRA_DUPLA, '')
    .replace(/^\s*-\s*|\s*-\s*$/g, '')
    .trim();
  if (maquina !== '1560') return semDupla;
  return semDupla
    ? `${NARRATIVA_COLADEIRA_DUPLA} - ${semDupla}`
    : NARRATIVA_COLADEIRA_DUPLA;
}

// ── Etapa 1: Upload ───────────────────────────────────────────────────────────

export function ProductRoutingContextBadge({ productId }) {
  if (productId == null) return null;
  return (
    <Badge variant="info" aria-label="Contexto Product">
      Vinculado ao Produto #{productId}
    </Badge>
  );
}

function EtapaUpload({ onPecasCarregadas, productId = null }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv', '.CSV'] },
    multiple: false,
  });

  const handleGerar = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      appendRoutingProductContext(formData, productId);
      const token = localStorage.getItem('token');
      const response = await fetch(
        '/api/roteiro/gerar',
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
      );
      if (!response.ok) {
        let detail = `Erro ${response.status}`;
        try { const j = await response.json(); if (j.detail) detail = j.detail; } catch (_) {}
        throw new Error(detail);
      }
      const data = await response.json();
      onPecasCarregadas(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="cw-page-title">Roteiro de Peças</h1>
      <p className="cw-page-subtitle">Gere o roteiro de operações a partir de uma planilha de composição</p>
      <ProductRoutingContextBadge productId={productId} />
      <div className="roteiro-gerar">
      <div
        {...getRootProps()}
        className={`roteiro-dropzone${isDragActive ? ' active' : ''}${selectedFile ? ' has-file' : ''}`}
      >
        <input {...getInputProps()} />
        {selectedFile ? (
          <div className="roteiro-file-info">
            <span className="roteiro-file-icon"></span>
            <div>
              <p className="roteiro-file-name">{selectedFile.name}</p>
              <p className="roteiro-file-size">{(selectedFile.size / 1024).toFixed(2)} KB</p>
            </div>
            <button className="roteiro-remove-btn" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setError(null); }}>✕</button>
          </div>
        ) : (
          <div className="roteiro-dropzone-content">
            <span className="roteiro-upload-icon"></span>
            <p className="roteiro-upload-text">{isDragActive ? 'Solte o arquivo aqui...' : 'Arraste um arquivo .csv ou clique para selecionar'}</p>
            <p className="roteiro-upload-hint">Arquivo CSV exportado do Senior</p>
          </div>
        )}
      </div>

      {selectedFile && (
        <div className="roteiro-actions">
          <button className="roteiro-btn-primary" onClick={handleGerar} disabled={loading}>
            {loading ? <><span className="roteiro-spinner" /> Carregando...</> : 'Gerar Preview'}
          </button>
        </div>
      )}

      {error && (
        <div className="roteiro-error">
          <strong>Erro</strong>
          <p>{error}</p>
        </div>
      )}
      </div>
    </>
  );
}

// ── Modal de adicionar operação ───────────────────────────────────────────────

function maquinasDaOperacao(codigo, maquinaAtual, maquinasPermitidas) {
  const configuradas = maquinasPermitidas[codigo] || [];
  if (configuradas.includes(maquinaAtual)) return configuradas;
  return maquinaAtual ? [maquinaAtual, ...configuradas] : configuradas;
}

function ModalAdicionarOp({ listaOps, maquinasPermitidas, onConfirmar, onCancelar }) {
  const [opIdx, setOpIdx] = useState(0);
  const [narrativa, setNarrativa] = useState('');
  const [narrativaManual, setNarrativaManual] = useState(false);
  const [maquina, setMaquina] = useState(listaOps[0]?.maquina || '');
  const opSelecionada = listaOps[opIdx];
  const usinagemSelecionada = operacaoEhUsinagem(opSelecionada?.operacao);
  const maquinasDisponiveis = maquinasDaOperacao(
    opSelecionada?.operacao,
    maquina,
    maquinasPermitidas
  );

  const handleConfirmar = () => {
    const op = opSelecionada;
    onConfirmar({
      origin_kind: 'user_added',
      seq: 0, // será recalculado no export
      operacao: op.operacao,
      descr_operacao: op.descr,
      multiplos: op.multiplos,
      multiplos_override: null,
      maquina,
      narrativa: normalizarNarrativa(narrativa),
      tempo_prop: '1,00000',
      fornecedor: 0,
      cod_servico: '',
      opcao: 1,
      tipos: 1,
      mov_orp: '',
      mov_orp_override: null,
    });
  };

  return (
    <div className="roteiro-modal-overlay" onClick={onCancelar}>
      <div className="roteiro-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Adicionar Operação</h3>

        <label className="roteiro-modal-label">Operação</label>
        <select
          className="roteiro-modal-select"
          value={opIdx}
          onChange={(e) => {
            setOpIdx(Number(e.target.value));
            setMaquina(listaOps[Number(e.target.value)]?.maquina || '');
            setNarrativa('');
            setNarrativaManual(false);
          }}
        >
          {listaOps.map((op, i) => (
            <option key={i} value={i}>{op.operacao} — {op.descr}</option>
          ))}
        </select>

        <label className="roteiro-modal-label">Máquina</label>
        <select
          className="roteiro-modal-select"
          value={maquina}
          onChange={(e) => {
            const novaMaquina = e.target.value;
            setMaquina(novaMaquina);
            setNarrativa(narrativaParaMaquina(
              { ...opSelecionada, narrativa },
              novaMaquina
            ));
          }}
          disabled={maquinasDisponiveis.length <= 1}
        >
          {maquinasDisponiveis.map((codigo) => (
            <option key={codigo} value={codigo}>{rotuloMaquina(codigo)}</option>
          ))}
        </select>

        <label className="roteiro-modal-label">Narrativa <span className="roteiro-modal-hint">(opcional)</span></label>
        {usinagemSelecionada && (
          <select
            className="roteiro-modal-select"
            value={narrativaManual ? OPCAO_NARRATIVA_MANUAL : narrativa}
            onChange={(e) => {
              if (e.target.value === OPCAO_NARRATIVA_MANUAL) {
                setNarrativa('');
                setNarrativaManual(true);
              } else {
                setNarrativa(e.target.value);
                setNarrativaManual(false);
              }
            }}
          >
            <option value="">Selecione uma narrativa</option>
            {NARRATIVAS_USINAGEM.map((opcao) => (
              <option key={opcao} value={opcao}>{opcao}</option>
            ))}
            <option value={OPCAO_NARRATIVA_MANUAL}>OUTRA NARRATIVA</option>
          </select>
        )}
        {(!usinagemSelecionada || narrativaManual) && (
          <input
            className="roteiro-modal-input roteiro-texto-maiusculo"
            type="text"
            value={narrativa}
            onChange={(e) => setNarrativa(e.target.value.replace(/[;\r\n\t]+/g, ' '))}
            placeholder={usinagemSelecionada ? 'Digite outra narrativa' : 'Ex: COR PRETA'}
          />
        )}

        <div className="roteiro-modal-actions">
          <button className="roteiro-btn-secondary" onClick={onCancelar}>Cancelar</button>
          <button className="roteiro-btn-primary" onClick={handleConfirmar}>Adicionar</button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers de multiplos ──────────────────────────────────────────────────────

const RECORTES = new Set(["027", "027.2", "039", "039.2", "039.3"]);

function recalcularMultiplos(operacoes, mult) {
  let aposRecorte = false;
  return operacoes.map((op) => {
    if (op.operacao === "032") return { ...op, multiplos: 1 };
    const novoMult = aposRecorte ? 1 : mult;
    if (RECORTES.has(op.operacao)) aposRecorte = true;
    return { ...op, multiplos: novoMult };
  });
}

function avisosNarrativaPeca(peca) {
  return peca.operacoes
    .map((op) => ({ op, mensagem: alertaNarrativa(op) }))
    .filter(({ mensagem }) => mensagem)
    .map(({ op, mensagem }) => `${op.operacao} — ${mensagem}`);
}

export function routingStateFingerprint(pecas, familia, embalagem) {
  return JSON.stringify({
    pecas: pecas.map(({ expandido, ...peca }) => peca),
    familia,
    embalagem,
  });
}

export function routingExportPayload(
  routingIdentity,
  pecas,
  familia,
  embalagem
) {
  return { ...routingIdentity, pecas, familia, embalagem };
}

export function routingIdentityFromResponse(response, previousIdentity = null) {
  const identity = {
    routing_run_id: response.routing_run_id,
    lock_version: response.lock_version,
    revision_number: response.revision_number ?? null,
  };
  if (Object.prototype.hasOwnProperty.call(response, 'product_id')) {
    identity.product_id = response.product_id;
  } else if (
    previousIdentity &&
    Object.prototype.hasOwnProperty.call(previousIdentity, 'product_id')
  ) {
    identity.product_id = previousIdentity.product_id;
  }
  return identity;
}

export function productIdFromSearch(search) {
  const raw = new URLSearchParams(search).get('product_id');
  if (!raw || !/^\d+$/.test(raw)) return null;
  const productId = Number(raw);
  return Number.isSafeInteger(productId) && productId > 0 ? productId : null;
}

export function appendRoutingProductContext(formData, productId) {
  if (productId != null) {
    formData.append('product_id', String(productId));
  }
  return formData;
}

export function routingEditorStateFromResponse(response) {
  return {
    pecas: response.pecas,
    familia: response.familia || '',
    embalagem: response.embalagem || {
      nome_produto: '',
      cod_produto_81: '',
      volumes: 1,
    },
    routingIdentity: routingIdentityFromResponse(response),
  };
}

export async function fetchRoutingRun(routingRunId, fetchImpl = fetch) {
  const token = localStorage.getItem('token');
  const response = await fetchImpl(`/api/roteiro/${routingRunId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    let detail = `Erro ${response.status}`;
    try {
      const data = await response.json();
      if (data.detail) detail = data.detail;
    } catch (_) {}
    throw new Error(detail);
  }
  return response.json();
}

export function mergeSavedRoutingPieces(currentPieces, savedPieces) {
  return savedPieces.map((piece) => ({
    ...piece,
    expandido: currentPieces.find(
      (current) => current.piece_instance_id === piece.piece_instance_id
    )?.expandido ?? true,
  }));
}

// ── Etapa 2: Editor ───────────────────────────────────────────────────────────

function EtapaEditor({
  pecasIniciais,
  familiaInicial,
  embalagemInicial,
  routingIdentity,
  onNovoUpload,
  listaOps,
  maquinasPermitidas,
}) {
  const [confirmEl, confirm] = useConfirm();
  const [pecas, setPecas] = useState(
    [...pecasIniciais]
      .sort((a, b) => String(a.cod_peca ?? '').localeCompare(String(b.cod_peca ?? ''), undefined, { numeric: true, sensitivity: 'base' }))
      .map((p) => ({ ...p, expandido: true }))
  );
  const [modalPecaIdx, setModalPecaIdx] = useState(null);
  const [baixandoTxt, setBaixandoTxt] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [currentRoutingIdentity, setCurrentRoutingIdentity] = useState(routingIdentity);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [mostrarVoltarTopo, setMostrarVoltarTopo] = useState(false);
  const [erro, setErro] = useState(null);
  const [editandoNarr, setEditandoNarr] = useState(null); // { pecaIdx, opIdx }
  const [valorNarr, setValorNarr] = useState('');
  const [editandoMult, setEditandoMult] = useState(null); // { pecaIdx, opIdx }
  const [valorMult, setValorMult] = useState('');
  const [arrastandoOp, setArrastandoOp] = useState(null); // { pecaIdx, opIdx }
  const [destinoArraste, setDestinoArraste] = useState(null); // { pecaIdx, opIdx }
  const [familia, setFamilia] = useState(familiaInicial);
  const [embalagem, setEmbalagem] = useState(embalagemInicial);
  const currentFingerprint = useMemo(
    () => routingStateFingerprint(pecas, familia, embalagem),
    [pecas, familia, embalagem]
  );
  const [savedFingerprint, setSavedFingerprint] = useState(currentFingerprint);
  const isDirty = currentFingerprint !== savedFingerprint;

  useEffect(() => {
    const atualizarVisibilidade = () => setMostrarVoltarTopo(window.scrollY > 400);
    atualizarVisibilidade();
    window.addEventListener('scroll', atualizarVisibilidade, { passive: true });
    return () => window.removeEventListener('scroll', atualizarVisibilidade);
  }, []);

  const toggleExpansao = (idx) => {
    setPecas((prev) => prev.map((p, i) => i === idx ? { ...p, expandido: !p.expandido } : p));
  };

  const moverOp = (pecaIdx, opIdx, direcao) => {
    setPecas((prev) => {
      const novas = prev.map((p) => ({ ...p, operacoes: [...p.operacoes] }));
      const peca = novas[pecaIdx];
      const ops = peca.operacoes;
      const alvo = opIdx + direcao;
      if (alvo < 0 || alvo >= ops.length) return prev;
      [ops[opIdx], ops[alvo]] = [ops[alvo], ops[opIdx]];
      peca.operacoes = recalcularMultiplos(ops, peca.mult ?? 1);
      return novas;
    });
  };

  const soltarOp = (pecaIdx, destinoIdx) => {
    if (!arrastandoOp || arrastandoOp.pecaIdx !== pecaIdx) return;
    const origemIdx = arrastandoOp.opIdx;
    if (origemIdx === destinoIdx) return;
    setPecas((prev) => {
      const novas = prev.map((p) => ({ ...p, operacoes: [...p.operacoes] }));
      const peca = novas[pecaIdx];
      const [movida] = peca.operacoes.splice(origemIdx, 1);
      peca.operacoes.splice(destinoIdx, 0, movida);
      peca.operacoes = recalcularMultiplos(peca.operacoes, peca.mult ?? 1);
      return novas;
    });
  };

  const encerrarArraste = () => {
    setArrastandoOp(null);
    setDestinoArraste(null);
  };

  const excluirOp = (pecaIdx, opIdx) => {
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx ? { ...p, operacoes: p.operacoes.filter((_, j) => j !== opIdx) } : p
    ));
  };

  const iniciarEditNarr = (pecaIdx, opIdx, valor) => {
    setEditandoNarr({ pecaIdx, opIdx });
    setValorNarr(valor);
  };

  const salvarNarr = () => {
    if (!editandoNarr) return;
    const { pecaIdx, opIdx } = editandoNarr;
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx
        ? { ...p, operacoes: p.operacoes.map((op, j) => j === opIdx ? { ...op, narrativa: normalizarNarrativa(valorNarr) } : op) }
        : p
    ));
    setEditandoNarr(null);
  };

  const atualizarNarrativa = (pecaIdx, opIdx, narrativa) => {
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx
        ? { ...p, operacoes: p.operacoes.map((op, j) => j === opIdx ? { ...op, narrativa } : op) }
        : p
    ));
  };

  const iniciarEditMult = (pecaIdx, opIdx, valor) => {
    setEditandoMult({ pecaIdx, opIdx });
    setValorMult(String(valor));
  };

  const salvarMult = () => {
    if (!editandoMult) return;
    const { pecaIdx, opIdx } = editandoMult;
    const numero = Number.parseInt(valorMult, 10);
    if (!Number.isInteger(numero) || numero < 1) {
      setErro('O múltiplo manual deve ser um número inteiro maior que zero');
      return;
    }
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx
        ? {
            ...p,
            operacoes: p.operacoes.map((op, j) =>
              j === opIdx
                ? { ...op, multiplos_override: numero === op.multiplos ? null : numero }
                : op
            ),
          }
        : p
    ));
    setEditandoMult(null);
    setErro(null);
  };

  const limparMultManual = (pecaIdx, opIdx) => {
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx
        ? {
            ...p,
            operacoes: p.operacoes.map((op, j) =>
              j === opIdx ? { ...op, multiplos_override: null } : op
            ),
          }
        : p
    ));
  };

  const atualizarMaquina = (pecaIdx, opIdx, maquina) => {
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx
        ? {
            ...p,
            operacoes: p.operacoes.map((op, j) =>
              j === opIdx
                ? { ...op, maquina, narrativa: narrativaParaMaquina(op, maquina) }
                : op
            ),
          }
        : p
    ));
  };

  const toggleMovOrp = (pecaIdx, opIdx) => {
    setPecas((prev) => prev.map((p, i) =>
      i !== pecaIdx ? p : {
        ...p,
        operacoes: p.operacoes.map((op, j) => {
          if (j !== opIdx) return op;
          const next =
            op.mov_orp_override === null ? 'S' :
            op.mov_orp_override === 'S'  ? 'N' : null;
          return { ...op, mov_orp_override: next };
        }),
      }
    ));
  };

  const adicionarOp = (pecaIdx, novaOp) => {
    setPecas((prev) => prev.map((p, i) =>
      i === pecaIdx ? { ...p, operacoes: [...p.operacoes, novaOp] } : p
    ));
    setModalPecaIdx(null);
  };

  const handleSalvar = async () => {
    if (!currentRoutingIdentity || !isDirty) return;
    setSalvando(true);
    setErro(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/roteiro/${currentRoutingIdentity.routing_run_id}/revisions`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lock_version: currentRoutingIdentity.lock_version,
            pecas,
            familia,
            embalagem,
          }),
        }
      );
      if (!response.ok) {
        let detail = `Erro ${response.status}`;
        try { const data = await response.json(); if (data.detail) detail = data.detail; } catch (_) {}
        throw new Error(detail);
      }
      const saved = await response.json();
      const savedPieces = mergeSavedRoutingPieces(pecas, saved.pecas);
      setPecas(savedPieces);
      setCurrentRoutingIdentity(
        routingIdentityFromResponse(saved, currentRoutingIdentity)
      );
      setSavedFingerprint(routingStateFingerprint(savedPieces, familia, embalagem));
      setHistoryRefreshKey((value) => value + 1);
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const handleBaixarTxt = async () => {
    if (!familia) {
      setErro('Informe a família antes de gerar');
      return;
    }
    if (familia.length !== 6 || !familia.startsWith('51') || !/^\d+$/.test(familia)) {
      setErro('Família inválida — informe 6 dígitos começando com 51 (ex: 510590)');
      return;
    }
    if (!embalagem.nome_produto.trim()) {
      setErro('Informe o nome do produto da embalagem');
      return;
    }
    if (!embalagem.cod_produto_81.trim()) {
      setErro('Informe o código 81 do produto');
      return;
    }
    if (embalagem.cod_produto_81.length !== 9) {
      setErro('Código 81 inválido — informe 9 dígitos (ex: 810006109)');
      return;
    }
    if (!embalagem.volumes || embalagem.volumes < 1) {
      setErro('Informe ao menos 1 volume');
      return;
    }
    const manuais = pecas.flatMap((p) =>
      p.operacoes
        .filter((op) => op.mov_orp_override !== null && op.mov_orp_override !== undefined)
        .map((op) => `${p.cod_peca} · seq ${op.seq} · ${op.operacao} → ${op.mov_orp_override}`)
    );
    if (manuais.length > 0) {
      const ok = await confirm({
        title: 'Operações com MOV manual',
        message: `${manuais.length} operação(ões) com MOV manual:\n\n${manuais.join('\n')}\n\nGerar mesmo assim?`,
        confirmLabel: 'Gerar mesmo assim',
      });
      if (!ok) return;
    }
    const multiplosManuais = pecas.flatMap((p) =>
      p.operacoes
        .map((op, opIdx) => ({ op, opIdx }))
        .filter(({ op }) => op.multiplos_override !== null && op.multiplos_override !== undefined)
        .map(({ op, opIdx }) => (
          `${p.cod_peca} · seq ${(opIdx + 1) * 10} · ${op.operacao} → ${op.multiplos_override}`
        ))
    );
    if (multiplosManuais.length > 0) {
      const ok = await confirm({
        title: 'Operações com múltiplo manual',
        message: `${multiplosManuais.length} operação(ões) com múltiplo manual:\n\n${multiplosManuais.join('\n')}\n\nGerar mesmo assim?`,
        confirmLabel: 'Gerar mesmo assim',
      });
      if (!ok) return;
    }
    setBaixandoTxt(true);
    setErro(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        '/api/roteiro/exportar',
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(routingExportPayload(
            currentRoutingIdentity,
            pecas,
            familia,
            embalagem
          )),
        }
      );
      if (!response.ok) {
        let detail = `Erro ${response.status}`;
        try { const j = await response.json(); if (j.detail) detail = j.detail; } catch (_) {}
        throw new Error(detail);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${embalagem.cod_produto_81}_ROTEIRO.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setErro(err.message);
    } finally {
      setBaixandoTxt(false);
    }
  };

  const totalOps = pecas.reduce((acc, p) => acc + p.operacoes.length, 0);
  const totalAvisos = pecas.filter((p) =>
    (p.avisos && p.avisos.length > 0) || avisosNarrativaPeca(p).length > 0
  ).length;

  return (
    <div className="roteiro-editor">
      {confirmEl}
      <div className="roteiro-editor-topbar">
        <button className="roteiro-btn-ghost" onClick={onNovoUpload}>← Novo upload</button>
        <div className="roteiro-editor-title">
          <strong>{pecas.length}</strong> peças
          {totalAvisos > 0 && <> &nbsp;·&nbsp; <strong className="roteiro-aviso-count">{totalAvisos} com avisos</strong></>}
          &nbsp;·&nbsp; <strong>{totalOps}</strong> operações
        </div>
        <ProductRoutingContextBadge
          productId={currentRoutingIdentity?.product_id}
        />
        {currentRoutingIdentity?.routing_run_id && (
          <Button
            variant="secondary"
            className="cw-routing-history-trigger"
            onClick={() => setHistoryOpen(true)}
          >
            Histórico
          </Button>
        )}
        <button
          className="roteiro-btn-secondary"
          onClick={handleSalvar}
          disabled={salvando || !isDirty}
        >
          {salvando ? <><span className="roteiro-spinner" /> Salvando...</> : 'Salvar'}
        </button>
        <button className="roteiro-btn-primary" onClick={handleBaixarTxt} disabled={baixandoTxt}>
          {baixandoTxt ? <><span className="roteiro-spinner" /> Exportando...</> : 'Baixar TXT'}
        </button>
      </div>
      {historyOpen && currentRoutingIdentity?.routing_run_id && (
        <RoutingHistoryPanel
          runId={currentRoutingIdentity.routing_run_id}
          refreshKey={historyRefreshKey}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* Painel de embalagem */}
      <div className="roteiro-embalagem-panel">
        <div className="roteiro-embalagem-fields">
          <div className="roteiro-emb-row">
            <div className="roteiro-emb-field roteiro-emb-field--familia">
              <label>Família</label>
              <input
                type="text"
                value={familia}
                onChange={(e) => setFamilia(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Ex: 510590"
                maxLength={6}
              />
            </div>
            <div className="roteiro-emb-field roteiro-emb-field--wide">
              <label>Nome do produto</label>
              <input
                type="text"
                value={embalagem.nome_produto}
                onChange={(e) => setEmbalagem((prev) => ({ ...prev, nome_produto: e.target.value }))}
                placeholder="Ex: EXPOSITOR RACK (2.0)"
              />
            </div>
            <div className="roteiro-emb-field">
              <label>Código 81 do produto</label>
              <input
                type="text"
                value={embalagem.cod_produto_81}
                onChange={(e) =>
                  setEmbalagem((prev) => ({
                    ...prev,
                    cod_produto_81: e.target.value.replace(/\D/g, '').slice(0, 9),
                  }))
                }
                placeholder="Ex: 810006109"
                maxLength={9}
              />
            </div>
            <div className="roteiro-emb-field roteiro-emb-field--narrow">
              <label>Volumes</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={embalagem.volumes}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\D/g, '').slice(0, 2);
                  setEmbalagem((prev) => ({ ...prev, volumes: valor }));
                }}
                onBlur={() => setEmbalagem((prev) => ({
                  ...prev,
                  volumes: prev.volumes === ''
                    ? 1
                    : Math.min(99, Math.max(1, Number(prev.volumes))),
                }))}
              />
            </div>
          </div>
        </div>
      </div>

      {erro && (
        <div className="roteiro-error roteiro-error-inline">
          <strong>Erro</strong><p>{erro}</p>
        </div>
      )}

      <div className="roteiro-tabela">
        {pecas.map((peca, pecaIdx) => (
          <div key={peca.cod_peca} className="roteiro-peca-bloco">
            {/* Cabeçalho da peça */}
            <div className="roteiro-peca-header" onClick={() => toggleExpansao(pecaIdx)}>
              <span className="roteiro-peca-chevron">{peca.expandido ? '▼' : '▶'}</span>
              <span className="roteiro-peca-cod">{peca.cod_peca}</span>
              <span className="roteiro-peca-nome">{peca.nome_peca}</span>
              {((peca.avisos && peca.avisos.length > 0) || avisosNarrativaPeca(peca).length > 0) && (
                <span
                  className="roteiro-peca-aviso-badge"
                  title={[...(peca.avisos || []), ...avisosNarrativaPeca(peca)].join('\n')}
                ></span>
              )}
              <span className="roteiro-peca-contador">({peca.operacoes.length} operações)</span>
            </div>

            {/* Operações */}
            {peca.expandido && (
              <div className="roteiro-ops-container">
                {peca.avisos && peca.avisos.length > 0 && (
                  <div className="roteiro-avisos-bloco">
                    {peca.avisos.map((aviso, i) => (
                      <div key={i} className="roteiro-aviso-linha">{aviso}</div>
                    ))}
                  </div>
                )}
                <table className="roteiro-ops-tabela">
                  <thead>
                    <tr>
                      <th className="col-drag" aria-label="Reordenar" />
                      <th className="col-seq">Seq</th>
                      <th className="col-op">Op.</th>
                      <th className="col-descr">Descrição</th>
                      <th className="col-maq">Máquina</th>
                      <th className="col-servico">Cód. Serviço</th>
                      <th className="col-fornecedor">Fornecedor</th>
                      <th className="col-mult">Pulso</th>
                      <th className="col-mov">MOV</th>
                      <th className="col-narr">Narrativa</th>
                      <th className="col-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {peca.operacoes.map((op, opIdx) => (
                      <tr
                        key={opIdx}
                        className={
                          destinoArraste?.pecaIdx === pecaIdx && destinoArraste?.opIdx === opIdx
                            ? 'roteiro-op-destino'
                            : ''
                        }
                        onDragOver={(e) => {
                          if (arrastandoOp?.pecaIdx !== pecaIdx) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDestinoArraste({ pecaIdx, opIdx });
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          soltarOp(pecaIdx, opIdx);
                          encerrarArraste();
                        }}
                      >
                        <td className="col-drag">
                          <span
                            className="roteiro-drag-handle"
                            draggable
                            role="button"
                            tabIndex="0"
                            title="Arraste para reordenar"
                            aria-label={`Arrastar operação ${op.operacao}`}
                            onDragStart={(e) => {
                              setArrastandoOp({ pecaIdx, opIdx });
                              setDestinoArraste({ pecaIdx, opIdx });
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', `${pecaIdx}:${opIdx}`);
                            }}
                            onDragEnd={encerrarArraste}
                          >⋮⋮</span>
                        </td>
                        <td className="col-seq">{(opIdx + 1) * 10}</td>
                        <td className="col-op">{op.operacao}</td>
                        <td className="col-descr">{op.descr_operacao}</td>
                        <td className="col-maq">
                          {(() => {
                            const maquinas = maquinasDaOperacao(
                              op.operacao,
                              op.maquina,
                              maquinasPermitidas
                            );
                            return maquinas.length > 1 ? (
                              <span className="roteiro-maquina-select-wrap">
                                <span className="roteiro-maquina-codigo" aria-hidden="true">
                                  {op.maquina}
                                </span>
                                <select
                                  className="roteiro-maquina-select roteiro-maquina-select--codigo-fechado"
                                  value={op.maquina}
                                  aria-label={`Máquina da operação ${op.operacao}`}
                                  onChange={(e) => atualizarMaquina(pecaIdx, opIdx, e.target.value)}
                                >
                                  {maquinas.map((codigo) => (
                                    <option key={codigo} value={codigo}>{rotuloMaquina(codigo)}</option>
                                  ))}
                                </select>
                              </span>
                            ) : op.maquina;
                          })()}
                        </td>
                        <td className="col-servico">{op.cod_servico || '—'}</td>
                        <td className="col-fornecedor">{op.fornecedor || '—'}</td>
                        <td
                          className="col-mult col-mult-edit"
                          title="Clique para definir um múltiplo manual"
                          onClick={() => {
                            if (!editandoMult) {
                              iniciarEditMult(
                                pecaIdx,
                                opIdx,
                                op.multiplos_override ?? op.multiplos
                              );
                            }
                          }}
                        >
                          {editandoMult?.pecaIdx === pecaIdx && editandoMult?.opIdx === opIdx ? (
                            <input
                              className="roteiro-mult-input"
                              autoFocus
                              inputMode="numeric"
                              value={valorMult}
                              onChange={(e) => setValorMult(e.target.value.replace(/\D/g, ''))}
                              onBlur={salvarMult}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') salvarMult();
                                if (e.key === 'Escape') setEditandoMult(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span>
                              {op.multiplos_override ?? op.multiplos}
                              {op.multiplos_override != null && (
                                <button
                                  type="button"
                                  className="roteiro-mult-reset"
                                  title="Voltar ao múltiplo automático"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    limparMultManual(pecaIdx, opIdx);
                                  }}
                                >✱</button>
                              )}
                            </span>
                          )}
                        </td>
                        <td
                          className="col-mov col-mov-edit"
                          title="clique: alterna S / N / automático"
                          onClick={() => toggleMovOrp(pecaIdx, opIdx)}
                        >
                          {(() => {
                            const isManual = op.mov_orp_override !== null && op.mov_orp_override !== undefined;
                            const exibido = isManual ? op.mov_orp_override : op.mov_orp;
                            if (exibido === 'S') return <span className={isManual ? 'roteiro-mov-s-manual' : 'roteiro-mov-s'}>{isManual ? 'S✱' : 'S'}</span>;
                            if (exibido === 'N') return <span className={isManual ? 'roteiro-mov-n-manual' : 'roteiro-mov-n'}>{isManual ? 'N✱' : 'N'}</span>;
                            return <span className="roteiro-mov-vazio">—</span>;
                          })()}
                        </td>
                        <td
                          className="col-narr col-narr-edit"
                          onClick={() => !editandoNarr && !operacaoEhUsinagem(op.operacao) && iniciarEditNarr(pecaIdx, opIdx, op.narrativa)}
                        >
                          {editandoNarr?.pecaIdx === pecaIdx && editandoNarr?.opIdx === opIdx ? (
                            <input
                              className="roteiro-narr-input roteiro-texto-maiusculo"
                              autoFocus
                              value={valorNarr}
                              onChange={(e) => setValorNarr(e.target.value.replace(/[;\r\n\t]+/g, ' '))}
                              onBlur={salvarNarr}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') salvarNarr();
                                if (e.key === 'Escape') setEditandoNarr(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : operacaoEhUsinagem(op.operacao) ? (
                            <div className="roteiro-narr-controle" onClick={(e) => e.stopPropagation()}>
                              <select
                                className="roteiro-narr-select"
                                value={
                                  !op.narrativa
                                    ? ''
                                    : narrativaUsinagemEhPredefinida(op.narrativa)
                                      ? op.narrativa
                                      : OPCAO_NARRATIVA_MANUAL
                                }
                                onChange={(e) => {
                                  if (e.target.value === OPCAO_NARRATIVA_MANUAL) {
                                    iniciarEditNarr(
                                      pecaIdx,
                                      opIdx,
                                      narrativaUsinagemEhPredefinida(op.narrativa) ? '' : op.narrativa
                                    );
                                  } else {
                                    atualizarNarrativa(pecaIdx, opIdx, e.target.value);
                                  }
                                }}
                              >
                                <option value="">Selecione...</option>
                                {NARRATIVAS_USINAGEM.map((opcao) => (
                                  <option key={opcao} value={opcao}>{opcao}</option>
                                ))}
                                <option value={OPCAO_NARRATIVA_MANUAL}>OUTRA NARRATIVA</option>
                              </select>
                              {op.narrativa && !narrativaUsinagemEhPredefinida(op.narrativa) && (
                                <button
                                  className="roteiro-narr-manual-btn"
                                  onClick={() => iniciarEditNarr(pecaIdx, opIdx, op.narrativa)}
                                >
                                  {op.narrativa}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className={op.narrativa ? '' : 'roteiro-narr-vazia'}>
                              {op.narrativa || 'clique para editar'}
                            </span>
                          )}
                          {alertaNarrativa(op) && (
                            <span className="roteiro-narr-alerta">{alertaNarrativa(op)}</span>
                          )}
                        </td>
                        <td className="col-acoes">
                          <button
                            className="roteiro-acao-btn"
                            title="Mover para cima"
                            disabled={opIdx === 0}
                            onClick={() => moverOp(pecaIdx, opIdx, -1)}
                          >↑</button>
                          <button
                            className="roteiro-acao-btn"
                            title="Mover para baixo"
                            disabled={opIdx === peca.operacoes.length - 1}
                            onClick={() => moverOp(pecaIdx, opIdx, 1)}
                          >↓</button>
                          <button
                            className="roteiro-acao-btn roteiro-acao-del"
                            title="Excluir"
                            onClick={() => excluirOp(pecaIdx, opIdx)}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <button
                  className="roteiro-btn-add-op"
                  onClick={() => setModalPecaIdx(pecaIdx)}
                >
                  + Adicionar operação
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {mostrarVoltarTopo && (
        <button
          type="button"
          className="roteiro-voltar-topo"
          aria-label="Voltar ao topo da página"
          title="Voltar ao topo"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
      )}

      {modalPecaIdx !== null && (
        <ModalAdicionarOp
          listaOps={listaOps}
          maquinasPermitidas={maquinasPermitidas}
          onConfirmar={(op) => adicionarOp(modalPecaIdx, op)}
          onCancelar={() => setModalPecaIdx(null)}
        />
      )}
    </div>
  );
}

// ── Componente raiz ───────────────────────────────────────────────────────────

function RoteiroGerar() {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState(1);
  const [routingState, setRoutingState] = useState(null);
  const [carregandoRun, setCarregandoRun] = useState(false);
  const [erroCarregamento, setErroCarregamento] = useState(null);
  const [listaOps, setListaOps] = useState(OPERACOES_LISTA);
  const [maquinasPermitidas, setMaquinasPermitidas] = useState({});
  const productIdContext = productIdFromSearch(window.location.search);
  const routingRunId = new URLSearchParams(window.location.search).get('routing_run_id');
  const linkedProductId = routingState?.routingIdentity?.product_id;
  const returnProductId = routingRunId
    ? (Number.isSafeInteger(linkedProductId) && linkedProductId > 0 ? linkedProductId : null)
    : productIdContext;
  const returnAction = returnProductId != null && (
    <div className="cw-routing-product-return">
      <Button variant="secondary" onClick={() => navigate(`/produtos/${returnProductId}`)}>
        <ArrowLeft size={16} aria-hidden="true" />
        Voltar ao Produto
      </Button>
    </div>
  );
  const metricsAction = (
    <div className="cw-routing-metrics-link">
      <Button variant="secondary" onClick={() => navigate('/roteiro/metricas')}>
        <BarChart3 size={16} aria-hidden="true" />
        Métricas de qualidade
      </Button>
    </div>
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/operacoes', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const ativas = data
          .filter((op) => op.ativo)
          .map((op) => ({ operacao: op.codigo, descr: op.descricao, maquina: op.maquina, multiplos: op.multiplos }));
        if (ativas.length > 0) {
          setListaOps(ativas);
        } else {
          console.warn('[RoteiroGerar] API retornou lista vazia — usando OPERACOES_LISTA como fallback');
        }
      })
      .catch((err) => {
        console.warn('[RoteiroGerar] Falha ao buscar operações do banco — usando OPERACOES_LISTA como fallback', err);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/roteiro/maquinas-permitidas', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setMaquinasPermitidas)
      .catch((err) => {
        console.error('[RoteiroGerar] Falha ao buscar máquinas permitidas:', err);
      });
  }, []);

  useEffect(() => {
    const routingRunId = new URLSearchParams(window.location.search).get(
      'routing_run_id'
    );
    if (!routingRunId) return undefined;

    let active = true;
    setCarregandoRun(true);
    setErroCarregamento(null);
    fetchRoutingRun(routingRunId)
      .then((response) => {
        if (!active) return;
        setRoutingState(routingEditorStateFromResponse(response));
        setEtapa(2);
      })
      .catch((err) => {
        if (!active) return;
        console.error('[RoteiroGerar] Falha ao reabrir roteiro:', err);
        setErroCarregamento(err.message);
      })
      .finally(() => {
        if (active) setCarregandoRun(false);
      });

    return () => { active = false; };
  }, []);

  const handlePecasCarregadas = (routingPreview) => {
    setRoutingState(routingEditorStateFromResponse(routingPreview));
    setErroCarregamento(null);
    setEtapa(2);
  };

  const handleNovoUpload = () => {
    const url = new URL(window.location.href);
    const linkedProductId = routingState?.routingIdentity?.product_id;
    url.searchParams.delete('routing_run_id');
    if (linkedProductId != null) {
      url.searchParams.set('product_id', String(linkedProductId));
    } else {
      url.searchParams.delete('product_id');
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    setRoutingState(null);
    setErroCarregamento(null);
    setEtapa(1);
  };

  if (etapa === 2 && routingState) {
    return (
      <>
        {returnAction}
        {metricsAction}
        <EtapaEditor
          pecasIniciais={routingState.pecas}
          familiaInicial={routingState.familia}
          embalagemInicial={routingState.embalagem}
          routingIdentity={routingState.routingIdentity}
          onNovoUpload={handleNovoUpload}
          listaOps={listaOps}
          maquinasPermitidas={maquinasPermitidas}
        />
      </>
    );
  }

  return (
    <>
      {returnAction}
      {metricsAction}
      {carregandoRun && <div className="roteiro-gerar">Carregando roteiro...</div>}
      {erroCarregamento && (
        <div className="roteiro-error roteiro-gerar">
          <strong>Erro</strong>
          <p>{erroCarregamento}</p>
        </div>
      )}
      {!carregandoRun && (
        <EtapaUpload
          onPecasCarregadas={handlePecasCarregadas}
          productId={productIdContext}
        />
      )}
    </>
  );
}

export default RoteiroGerar;
